import { useRef, Suspense, useState, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Physics, usePlane, useBox } from '@react-three/cannon'
import { useTexture, Environment, OrbitControls } from '@react-three/drei'
import gsap from 'gsap'
import randomColor from './Colors'

const CAMERA_START = 4
const MAX_BOXES = 26

function Plane(props) {
	const [ref] = usePlane(() => ({ rotation: [-Math.PI / 2, 0, 0], ...props }))

	return (
		<mesh ref={ref} receiveShadow>
			<planeBufferGeometry args={[100, 100]} />
			<meshStandardMaterial color='#ffffff' />
		</mesh>
	)
}

function Cube({ texture = null, count = 0, color = '#ffffff' }) {
	const rotation = useRef(Math.PI * Math.random())
	const [ref] = useBox(() => ({
		mass: 1,
		rotation: [0, rotation.current, 0],
		position: [0, 12 + count, 0],
		args: [4, 1, 4],
	}))
	return (
		<mesh
			ref={ref}
			rotation={[0, rotation.current, 0]}
			castShadow
			receiveShadow
		>
			<boxBufferGeometry args={[4, 1, 4]} />
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
			/>
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
			/>
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
				map={texture}
			/>
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
				map={texture}
			/>
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
			/>
			<meshStandardMaterial
				attachArray='material'
				color={color}
				metalness={0.8}
				roughness={0.1}
			/>
		</mesh>
	)
}

function Scene() {
	const group = useRef()
	const white = useRef()
	const { camera } = useThree()
	const [boxes, setBoxes] = useState([randomColor()])
	const boxCount = useRef(1)
	const interval = useRef()

	const t1 = useTexture('texture.jpg')

	const addBox = () => {
		let destination = camera.position.y + 1
		boxCount.current++

		if (boxCount.current <= MAX_BOXES) {
			setBoxes((s) => [...s, randomColor()])
			gsap.to(camera.position, {
				y: destination,
				duration: 1,
				ease: 'power4.out',
			})

			gsap.to(white.current.position, {
				y: destination - 1,
				duration: 1,
				ease: 'power4.out',
			})
		} else {
			clearInterval(interval.current)

			gsap.to(white.current.material, {
				opacity: 1,
				duration: 1,
				ease: 'power4.out',
				onComplete: () => {
					boxCount.current = 0
					camera.position.set(0, CAMERA_START, 0)
					white.current.position.set(0, CAMERA_START - 1, 0)
					setBoxes([])
				},
			})
		}
	}

	useFrame(() => (group.current.rotation.y -= 0.001))

	useEffect(() => {
		if (boxes.length === 0) {
			interval.current = setInterval(addBox, 1000)
			gsap.to(white.current.material, {
				opacity: 0,
				duration: 1,
				ease: 'power4.out',
			})
		}
	}, [boxes])

	useEffect(() => {
		interval.current = setInterval(addBox, 1000)
		return () => clearInterval(interval.current)
	}, [])

	return (
		<>
			<Physics>
				<Plane />
				<group ref={group}>
					{boxes.map((box, b) => (
						<Cube key={b} count={b} color={box} texture={t1} />
					))}

					<spotLight
						position={[10, 20, 0]}
						intensity={1.5}
						castShadow
						shadow-mapSize-width={1024}
						shadow-mapSize-height={1024}
					/>
				</group>
			</Physics>
			<mesh
				ref={white}
				rotation={[-Math.PI / 2, 0, 0]}
				position={[0, CAMERA_START - 1, 0]}
			>
				<planeBufferGeometry args={[100, 100]} />
				<meshBasicMaterial color='#ffffff' transparent opacity={0} />
			</mesh>
		</>
	)
}
function App() {
	return (
		<>
			<Canvas shadows camera={{ position: [0, CAMERA_START, 0] }}>
				<ambientLight intensity={0.1} />

				<Suspense fallback={null}>
					<Scene />
					<Environment path='./' files='env.hdr' background={false} />
					<mesh
						position={[0, 0.01, 0]}
						rotation={[-Math.PI / 2, 0, 0]}
						receiveShadow
					>
						<circleBufferGeometry args={[3, 64, 0]} />
						<meshStandardMaterial color='#cccccc' />
					</mesh>
				</Suspense>
				<OrbitControls />
			</Canvas>
		</>
	)
}

export default App
